import { z } from 'zod'

import { CurriculumSchema } from '~/@entities/Curriculum'
import { ConceptSortTypeSchema } from '~/@entities/MakeWorksheet'
import {
  LayoutColorSchema,
  PartitionTypeSchema,
  PdfDateTypeSchema,
} from '~/@entities/PrintDocument'
import { type Problem, ProblemSchema, ProblemV2WireSchema } from '~/@entities/Problem'
import { RevisionSchema, SchoolTypeSchema } from '~/@entities/SchoolSystem'
import { StudentIdSchema } from '~/@entities/Student'
import { WorksheetTagSchema } from '~/@entities/Worksheet'

// 오답학습지·교재 오답·쌍둥이/유사 추천(/derivation/*) 응답. 2026-10-13 백엔드 develop(cc5f60e17) derivation/model/dto·
// controller/response 로 확인.
//
// 문제는 서버 ProblemDto 를 그대로 싣는다(학습지·교재 문제는 @JsonUnwrapped 로 펼쳐 문제 칸 id 와 tagTop 이 붙는다).
// 답 단위·키패드를 answerUnitSet·keypadTypeSet 으로 부르는 모양이라 V2 와이어 스키마로 받아 fromProblemV2 로 푼다.

/***************** 학습지 레이아웃 *****************/
export const DerivationWorksheetLayoutSchema = z.object({
  schoolType: SchoolTypeSchema,
  grade: z.string(),
  title: z.string(),
  writer: z.string(),
  conceptSortType: ConceptSortTypeSchema,
  layoutType: z.number(),
  layoutColor: LayoutColorSchema,
  partitionType: PartitionTypeSchema,
  wrongAnswerNoteFlag: z.boolean(),
  conceptNameFlag: z.boolean(),
  answerRateFlag: z.boolean(),
  relationWorkbookFlag: z.boolean(),
  includeProblemFlag: z.boolean(),
  pdfDateType: PdfDateTypeSchema,
  pdfDate: z.iso.date().nullable(),
  tag: WorksheetTagSchema,
})
export type DerivationWorksheetLayout = z.infer<typeof DerivationWorksheetLayoutSchema>

/***************** 학습지 오답학습지 *****************/
export const WorksheetDerivationProblemWireSchema = ProblemV2WireSchema.extend({
  /** 틀린 문제를 그대로 싣는 경우의 학습지 문제 칸 id. 쌍둥이·유사로 바꾼 문제는 null */
  worksheetProblemId: z.number().nullable(),
  tagTop: z.string().nullable(),
})
export type WorksheetDerivationProblem = Problem & {
  worksheetProblemId: number | null
  tagTop: string | null
}

// 서버가 함께 내는 wrongProblemSearchDto(틀린 문제 원본 목록)는 아래 개수로만 쓰여 받지 않는다.
export const WorksheetDerivationWireSchema = z.object({
  problemList: z.array(WorksheetDerivationProblemWireSchema),
  worksheetLayout: DerivationWorksheetLayoutSchema,
  conceptIdList: z.array(z.number()),
  conceptSize: z.number(),
  problemIdList: z.array(z.number()),
  /** 서버가 줄 수 있는 오답학습지 문제 수 */
  problemSize: z.number(),
  wrongProblemCount: z.number(),
  wrongProblemConceptSize: z.number(),
  wrongCustomWorksheetProblemCount: z.number(),
  wrongPublicWorkbookProblemCount: z.number(),
  wrongSchoolWorkbookProblemCount: z.number(),
  wrongCustomWorkbookProblemCount: z.number(),
  wrongCustomSignatureWorkbookProblemCount: z.number(),
  wrongAcademyGroupWorkbookProblemCount: z.number(),
})
export type WorksheetDerivation = Omit<
  z.infer<typeof WorksheetDerivationWireSchema>,
  'problemList'
> & {
  problemList: WorksheetDerivationProblem[]
}

/***************** 교재 오답학습지 *****************/
export const WorkbookDerivationProblemWireSchema = ProblemV2WireSchema.extend({
  workbookProblemId: z.number(),
  tagTop: z.string().nullable(),
})
export type WorkbookDerivationProblem = Problem & {
  workbookProblemId: number
  tagTop: string | null
}

export const WorkbookDerivationWireSchema = z.object({
  /** 문제 추출의 기준이 된 문제 수 */
  baseProblemCount: z.number(),
  problemList: z.array(WorkbookDerivationProblemWireSchema),
  worksheetLayout: DerivationWorksheetLayoutSchema,
  conceptIdList: z.array(z.number()),
  conceptSize: z.number(),
  problemIdList: z.array(z.number()),
  problemSize: z.number(),
})
export type WorkbookDerivation = Omit<
  z.infer<typeof WorkbookDerivationWireSchema>,
  'problemList'
> & {
  problemList: WorkbookDerivationProblem[]
}

/***************** 쌍둥이·유사 문제 *****************/
// 서버 개념 캐시(CurriculumConceptCacheDto). 교육과정 노드에 유형·개정·학교급·학년이 더 붙는다.
// 그 밖의 캐시 키(topic·group 등)는 앱이 읽지 않아 받지 않는다.
export const DerivationConceptSchema = CurriculumSchema.extend({
  conceptId: z.number().nullable(),
  conceptName: z.string().nullable(),
  revision: RevisionSchema,
  schoolType: SchoolTypeSchema,
  grade: z.string(),
})
export type DerivationConcept = z.infer<typeof DerivationConceptSchema>

const PairSimilarCandidateWireSchema = z.object({
  problem: ProblemV2WireSchema,
  tagTop: z.string().nullable(),
  favorite: z.boolean().nullable(),
})
export type PairSimilarCandidate = {
  problem: Problem
  tagTop: string | null
  favorite: boolean | null
}

export const PairSimilarProblemsWireSchema = z.object({
  problem: ProblemV2WireSchema,
  curriculum: DerivationConceptSchema,
  pairProblemList: z.array(PairSimilarCandidateWireSchema),
  similarProblemList: z.array(PairSimilarCandidateWireSchema),
  favorite: z.boolean().nullable(),
})
export type PairSimilarProblems = {
  problem: Problem
  curriculum: DerivationConcept
  pairProblemList: PairSimilarCandidate[]
  similarProblemList: PairSimilarCandidate[]
  favorite: boolean | null
}

/***************** 취약 유형칩 추천 (유형분석) *****************/
// 2026-10-16 백엔드 develop(b1add2a8c) 의 derivation/controller 취약 유형칩 추천 응답과
// worksheet/controller/request/WeakConceptChipPersonalizedCustomWorksheetCreationRequest.kt·
// manager/WeakConceptChipPersonalizedCustomWorksheetCreationManager.kt 로 확인.

/** 그룹 공통형 추천 문제. 오답학습지 파이프라인이 학습지 문제 칸과 같은 자리에서 읽는다 */
export type WeakConceptChipRecommendedProblem = Problem & { tagTop: string | null }

export const GroupCommonWeakConceptChipRecommendationWireSchema = z.object({
  problemsWithTagTop: z.array(
    z.object({ problem: ProblemV2WireSchema, tagTop: z.string().nullable() }),
  ),
})

// 개인 맞춤형 추천은 화면이 비었는지만 보고 학습지 생성 요청에 그대로 되돌려 보낸다. 서버는 그 요청의 문제를
// ProblemResponseV2 전체로 역직렬화하므로(기본값 없는 필드가 빠지면 400) 도메인 Problem 으로 풀면 안 된다 —
// Problem 은 levelOfConceptChip 등을 버리고 답 단위의 이름을 바꾼다. 서버가 실제로 읽는 id·conceptId 만 선언하고
// 나머지 키는 looseObject 로 보존한다.
export const WeakConceptChipRecommendedProblemEchoSchema = z.object({
  problem: z.looseObject({
    id: ProblemSchema.shape.id,
    conceptId: ProblemSchema.shape.conceptId,
  }),
  tagTop: z.string().nullable(),
})
export type WeakConceptChipRecommendedProblemEcho = z.infer<
  typeof WeakConceptChipRecommendedProblemEchoSchema
>

export const PersonalizedWeakConceptChipRecommendationSchema = z.object({
  recommendedProblemsPerStudent: z.array(
    z.object({
      studentId: StudentIdSchema,
      problemsWithTagTop: z.array(WeakConceptChipRecommendedProblemEchoSchema),
    }),
  ),
})
export type PersonalizedWeakConceptChipRecommendation = z.infer<
  typeof PersonalizedWeakConceptChipRecommendationSchema
>
